import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';

import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Constants } from '../../shared/constants/create-survey.constants';
import { QuotaModalService } from '../quota-modal/quota-modal.service';
import { QuotaModalComponent } from '../quota-modal/quota-modal.component';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { LocationResItem } from '../../shared/interfaces/create-surveys.interface';
import {
  Flexible,
  Qualification,
  QuotaFlags,
  SelectedQuota,
} from '../../shared/interfaces/qualification.interface';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { AidaPayload } from '../../shared/interfaces/aida.interface';
import { AidaPayloadService } from '../../shared/services/aida/aida-payload/aida-payload.service';
import { notifyMessage } from '../../shared/constants/notify-message';
import { AidaSurveyQuotasEditInspector } from '../../shared/domain/aida-survey-quotas-edit-inspector';
import { QuotaFlagsComputer } from '../domain/quota-flags-computer';
import { sortDataByFieldName } from '../../shared/utils/sort-data';

@Component({
  selector: 'ps-ui-search-location-qualification',
  templateUrl: './search-location-qualification.component.html',
  styleUrls: ['./search-location-qualification.component.css'],
})
export class SearchLocationQualificationComponent implements OnInit, OnDestroy {
  private _subscription: Subscription = new Subscription();
  private _searchKeySub: Subject<string> = new Subject();
  public locationData: LocationResItem[] = [];
  public showDropDown = false;
  public searchKeyword: string = '';
  public showLoaderForSelectLocation: boolean = false;
  public quotaFlags: QuotaFlags = {
    add: true,
    edit: false,
    delete: false,
    census: false,
  };

  @Input() complete!: number;
  @Input() clickBalance!: number;
  @Input() quotas?: SelectedQuota[];
  @Input() flexible?: Flexible;
  @Input() qualification: Qualification = {} as Qualification;
  @Input() selectedCountry!: string;
  @Input() selectedLanguage!: string;
  @Input() deleteQuotas!: EventEmitter<string>;
  @Input() changeCompletes: EventEmitter<number> = new EventEmitter<number>();
  @Input() isQlcLiveSurvey: boolean = false;
  @Input() hasNestedGroup: boolean = false;
  @Input() isLiveSurveyEdit: boolean = false;
  @Output() selectionChange = new EventEmitter();
  @Output() readonly quotaChange = new EventEmitter();
  @Input() clearLocationData!: EventEmitter<string>;

  private _aida: AidaPayload = { enabled: false, selected_quota_ids: [] };
  private _isSelectedForAida: boolean = false;

  constructor(
    private _modal: ModalService,
    private _quotaModalService: QuotaModalService,
    private _buyerApiService: BuyerApiService,
    private _aidaPayloadService: AidaPayloadService
  ) {}

  ngOnInit(): void {
    // set default flexibility in case of undefined.
    this._handleAidaPayload();
    if (!this.flexible) {
      this.flexible = {
        enable: true,
        value: 0,
      };
    }

    const searchKeyRes = this._searchKeySub
      .pipe(
        // Again we can't wait for 1 second, we need to call every time. PD-8428
        filter((res) => res.length > 2) //,
        //debounceTime(1000)
      )
      .subscribe((searchTextValue) => {
        this.searchKeyword = searchTextValue;
        this._searchLocation();
      });
    this._subscription.add(searchKeyRes);
    this.deleteQuotas.subscribe((msg: string) => {
      this._deleteLocationQuotas();
    });
    this._setQuotaFlags();

    this.changeCompletes.subscribe((newCompletes) => {
      if (this.quotas) {
        this.quotas = this._quotaModalService.reDistributeQuotaQuantites(
          this.quotas,
          newCompletes,
          this.flexible
        );
        this.quotaChange.emit({
          msg: Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
          qualification: this.qualification,
          quotas: this.quotas,
          flexible: this.flexible,
        });
      }
    });

    this.clearLocationData.subscribe((msg: string) => {
      if (msg == 'ok') {
        this._clearLocationData();
      }
    });
  }

  private _handleAidaPayload() {
    const _aidaSubscription = this._aidaPayloadService
      .getAidaLiveEditPayload()
      .subscribe((payload) => {
        this._aida = payload;
        this._isSelectedForAida = new AidaSurveyQuotasEditInspector(
          this._aida,
          this.quotas,
          this.qualification.hasNestedQuotas
        ).notEditable();
      });

    this._subscription.add(_aidaSubscription);
  }

  _clearLocationData() {
    if (this.qualification?.data.length) {
      this.qualification.data = [];
      this.selectionChange.emit(this.qualification);
    }
  }

  private _setQuotaFlags() {
    const computer = new QuotaFlagsComputer(
      this.quotas || [],
      this.qualification
    );
    this.quotaFlags.add = computer.add();
    this.quotaFlags.edit = computer.edit();
    this.quotaFlags.delete = computer.delete();
    this.quotaFlags.census = computer.census();
  }

  private _searchLocation(flag?: string) {
    const qualificationId = this.qualification.db_qualification_code;
    let searchText = this.searchKeyword ? this.searchKeyword : '';
    this.showLoaderForSelectLocation = true;
    const locale = `${this.selectedLanguage}_${this.selectedCountry}`;
    // let excludeOptions = '';
    // if (flag === 'forAll') {
    //   // in case of select all click search keyword will be empty so it can fetch all.
    //   searchText = '';
    // } else {
    //   // we send excluded option only in case of text search  not in select all click.
    //   excludeOptions = this.qualification.data
    //     .map((item) => item.id)
    //     .toString();
    // }
    const excludeOptions = ''; // not using it for now.
    const locationDataSub = this._buyerApiService
      .searchQualificationOptions(
        qualificationId,
        locale,
        searchText,
        excludeOptions
      )
      .subscribe(
        (response: LocationResItem[]) => {
          this.locationData = response;
          if (flag === 'forAll' && response) {
            // only in case of select all we push into data
            this.qualification.data = response
              .filter((item) => item.id && item.name) // removing items with blank or null id or name
              .map((item) => {
                return {
                  id: item.id,
                  name: item.name,
                  selected: true,
                };
              });
            this.selectionChange.emit(this.qualification);
            this.showDropDown = false;
            this.searchKeyword = '';
            this.locationData = [];
          }
          this.showLoaderForSelectLocation = false;
        },
        (err) => {
          // We need to Work exactly in the same way we work in the old front ( calling the service every single keyup and not throwing error) PD-8428
          // this.locationData = [];
          // this._toastr.error(err.error.msg);
        }
      );
    this._subscription.add(locationDataSub);
  }

  onSearchKeyUp(searchTextValue: string) {
    this._searchKeySub.next(searchTextValue);
  }

  openDropDown() {
    this.showDropDown = true;
  }

  async selectLocation(value: { [key: string]: any }) {
    const [isProceed, deleteQuotaOnQualificationChange] =
      await this._quotaModalService.quotaConfirmationChecked(
        this.quotas,
        this.qualification.hasNestedQuotas
      );
    if (isProceed) {
      const idx = this.qualification.data.findIndex(
        (location) =>
          location.selected && Number(location.id) === Number(value.id)
      );
      value.selected = true;
      if (idx !== -1) {
        // exist
        this.qualification.data[idx] = value;
      } else {
        // not exist
        this.qualification.data.push(value);
      }
      this.selectionChange.emit(this.qualification);
    }
    this.showDropDown = false;
    this.searchKeyword = '';
    this.locationData = [];

    // delete quota if confirmed YES
    if (deleteQuotaOnQualificationChange) {
      if (this._isSelectedForAida) {
        this._aidaPayloadService.sendAidaError(
          notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
        );
        return;
      }
      this._actionAfterDeleteQuotas();
    }
  }

  async removeSelectedLocation(value: { [key: string]: any }) {
    if (this._isSelectedForAida) {
      this._aidaPayloadService.sendAidaError(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }

    const [isProceed, deleteQuotaOnQualificationChange] =
      await this._quotaModalService.quotaConfirmationChecked(
        this.quotas,
        this.qualification.hasNestedQuotas
      );
    if (isProceed) {
      this.qualification.data = this.qualification.data.filter(
        (location) => Number(location.id) !== Number(value.id)
      );
      this.selectionChange.emit(this.qualification);
    }
    // delete quota if confirmed YES
    if (deleteQuotaOnQualificationChange) {
      this._actionAfterDeleteQuotas();
    }
  }

  openSearchLocationQuotaModal() {
    if (this._quotaModalService.inValidComplete(this.complete)) {
      return;
    }

    this.qualification.data = this.qualification.data.map((data) => {
      data.text = data.name || data.text;
      return data;
    });
    sortDataByFieldName(this.quotas, 'text');
    const modalReferance = this._modal.open(QuotaModalComponent, {
      data: {
        quotas: this.quotas,
        complete: this.complete,
        flexible: this.flexible,
        clickBalance: this.clickBalance,
        qualification: this.qualification,
        hasNestedGroup: this.hasNestedGroup,
        isLiveSurveyEdit: this.isLiveSurveyEdit,
        isSelectedForAida: this._isSelectedForAida,
      },
      width: '80%',
    });

    modalReferance.onClose$.subscribe((res) => {
      if (
        [
          Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
          Constants.QUOTA_MODAL_ACTION.APPLY_NEST_QUOTA,
        ].includes(res?.msg)
      ) {
        this.quotas = res.quotas;
        this.flexible = res.flexible;
        this.quotaChange.emit(res);
        this.quotaFlags = {
          add: false,
          edit: true,
          census: false,
          delete: true,
        };
      }
    });
  }

  applySearchLocationCensus() {
    const isCompleteInvalid = this._quotaModalService.inValidComplete(
      this.complete
    );
    if (isCompleteInvalid) {
      return;
    }
    const distributedCensusQuotas = this._quotaModalService.distributeQuotas(
      this.qualification,
      this.complete,
      this.flexible
    );

    const censusQuotas = this._quotaModalService.adjustQuotaQuantites(
      distributedCensusQuotas,
      this.complete,
      this.flexible,
      true
    );

    if (censusQuotas.length && this.qualification?.census?.data?.length) {
      this.qualification.data = this.qualification?.census?.data.map((data) => {
        if (data.name) {
          data.text = data.name;
        }
        data.selected = true;
        data.id = data.condition_code;
        return data;
      });
      this.quotas = censusQuotas;
      this.quotaFlags = {
        add: false,
        edit: true,
        census: false,
        delete: true,
      };

      this.quotaChange.emit({
        msg: 'applyCensus',
        qualification: this.qualification,
        quotas: this.quotas,
        flexible: this.flexible,
      });
    }
  }

  _deleteLocationQuotas() {
    this._actionAfterDeleteQuotas();
  }

  deleteSearchLocationQuotas() {
    if (this._isSelectedForAida) {
      this._aidaPayloadService.sendAidaError(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }

    const modalReferance = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: Constants.CONFIRM_MODAL_TEXT.DELETE_QUOTA.replace(
          '<QUALIFICATION_NAME>',
          this.qualification.qualification_name
        ),
      },
    });

    modalReferance.onClose$.subscribe((res) => {
      if (res === 'ok') {
        this._actionAfterDeleteQuotas();
      }
    });
  }

  private _actionAfterDeleteQuotas() {
    this.quotas = [];
    this._resetQuotaFlags();
    this.quotaChange.emit({
      msg: 'deleteQuota',
      qualification: this.qualification,
      quotas: this.quotas,
      flexible: this.flexible,
    });
  }

  private _resetQuotaFlags() {
    this.quotaFlags = {
      add: true,
      edit: false,
      census: this._quotaModalService.setCensusFlag(this.qualification),
      delete: false,
    };
  }

  addAllQuotas() {
    this._searchLocation('forAll');
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }
}
